import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { PauseRounded, PlayArrowRounded, RefreshRounded } from '@mui/icons-material';
import Swal from 'sweetalert2';
import { listTunes, TuneItem } from '../services/api';

const API_BASE = (process.env.REACT_APP_BASE_URL || 'http://localhost:4000/api').replace(/\/+$/, '');
const API_ORIGIN = API_BASE.replace(/\/api$/i, '');
const absoluteUrl = (u?: string) => {
  if (!u) return '';
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith('/')) return `${API_ORIGIN}${u}`;
  return `${API_ORIGIN}/${u}`;
};

const displayUploader = (t?: TuneItem | null) =>
  t?.uploadedBy?.fullName || t?.uploadedBy?.username || t?.uploadedBy?.email || 'Unknown uploader';

const formatBytes = (size?: number) => {
  if (!size || size <= 0) return '—';
  const mb = size / (1024 * 1024);
  if (mb < 0.1) return '<0.1 MB';
  return `${mb.toFixed(mb >= 10 ? 0 : 1)} MB`;
};

const formatDate = (v?: string) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

const Tunes: React.FC = () => {
  const [tunes, setTunes] = useState<TuneItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [current, setCurrent] = useState<TuneItem | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listTunes();
      setTunes(Array.isArray(data) ? data : []);
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Failed to load tunes';
      Swal.fire('Error', msg, 'error');
      setTunes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return tunes;
    return tunes.filter((t) =>
      [t.title, t.artist, t.originalName, t.filename, displayUploader(t)].some((field) =>
        (field || '').toLowerCase().includes(term)
      )
    );
  }, [tunes, search]);

  const handlePlay = (tune: TuneItem) => {
    const audio = audioRef.current;

    if (current?._id === tune._id && audio) {
      if (audio.paused) {
        audio.play().catch(() => {});
      } else {
        audio.pause();
      }
      return;
    }

    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }

    setCurrent(tune);
    setIsPlaying(false);
    setTimeout(() => {
      audioRef.current?.play().catch(() => {});
    }, 20);
  };

  return (
    <Box>
      <Stack
        mb={3}
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>Tunes</Typography>
          <Typography color="text.secondary">Review uploads, play MP3s, and see who added them.</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            placeholder="Search title, artist, uploader..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button
            variant="outlined"
            startIcon={<RefreshRounded />}
            onClick={load}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      <Paper>
        {loading && <LinearProgress />}
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: '#F6A938' }}>
              <TableRow>
                <TableCell><strong>Title</strong></TableCell>
                <TableCell><strong>Artist</strong></TableCell>
                <TableCell><strong>MP3</strong></TableCell>
                <TableCell><strong>Uploaded By</strong></TableCell>
                <TableCell><strong>Uploaded At</strong></TableCell>
                <TableCell><strong>Size</strong></TableCell>
                <TableCell align="right"><strong>Play</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length > 0 ? (
                filtered.map((tune) => {
                  const isCurrent = current?._id === tune._id;
                  const playing = isCurrent && isPlaying;
                  return (
                    <TableRow key={tune._id}>
                      <TableCell sx={{ maxWidth: 220 }}>{tune.title || 'Untitled track'}</TableCell>
                      <TableCell>{tune.artist || '—'}</TableCell>
                      <TableCell sx={{ maxWidth: 240 }}>{tune.originalName || tune.filename}</TableCell>
                      <TableCell>{displayUploader(tune)}</TableCell>
                      <TableCell>{formatDate(tune.createdAt)}</TableCell>
                      <TableCell>{formatBytes(tune.size)}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          onClick={() => handlePlay(tune)}
                          color="primary"
                          disabled={!tune.fileUrl}
                        >
                          {playing ? <PauseRounded /> : <PlayArrowRounded />}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    {loading ? 'Loading tunes…' : 'No tunes found.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {current && (
        <Paper
          elevation={6}
          sx={{
            position: 'sticky',
            bottom: 0,
            mt: 3,
            borderRadius: 2,
            p: 1.5,
            backdropFilter: 'blur(8px)',
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={700} noWrap>
                {current.title || 'Untitled track'}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {current.artist || current.originalName || current.filename || 'Unknown track'}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                Uploaded by {displayUploader(current)} · {formatDate(current.createdAt)}
              </Typography>
            </Box>
            <audio
              controls
              ref={audioRef}
              src={absoluteUrl(current.fileUrl)}
              style={{ width: '100%' }}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={() => setIsPlaying(false)}
            >
              Your browser does not support the audio element.
            </audio>
          </Stack>
        </Paper>
      )}
    </Box>
  );
};

export default Tunes;
